import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { API, useAuth } from "../App";
import { ArrowUp, ArrowLeft, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = { domande: "Domande", idee: "Idee", esperienze: "Esperienze", scambi: "Scambi" };

const formatTime = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export default function ForumPost() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    const r = await axios.get(`${API}/forum/${id}`);
    setData(r.data);
  };
  useEffect(() => { load(); }, [id]);

  const upvote = async () => {
    if (!user) { toast.info("Accedi per votare"); return; }
    try { await axios.post(`${API}/forum/${id}/upvote`); load(); } catch (e) { console.error("Upvote failed:", e); }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/forum/${id}/replies`, { body: reply });
      setReply("");
      load();
    } catch { toast.error("Errore invio"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="py-32 text-center text-tc-muted italic font-serif text-xl">Carico…</div>;
  const { post, replies } = data;

  return (
    <main className="max-w-3xl mx-auto px-6 md:px-12 py-16" data-testid="forum-post-page">
      <Link to="/forum" className="inline-flex items-center gap-2 text-sm text-tc-muted hover:text-tc-primary mb-8" data-testid="back-forum">
        <ArrowLeft className="w-4 h-4" /> Forum
      </Link>

      <article className="bg-tc-surface rounded-3xl p-8 border border-tc/50 mb-10">
        <div className="flex items-start gap-4">
          <div className="flex flex-col items-center">
            <button onClick={upvote} data-testid="post-upvote">
              <ArrowUp className={`w-6 h-6 ${post.upvoted_by?.includes(user?.user_id) ? 'text-tc-secondary' : 'text-tc-muted hover:text-tc-primary'}`} />
            </button>
            <span className="text-tc-primary font-medium">{post.upvote_count || 0}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-tc-muted mb-2 flex gap-2 items-center flex-wrap">
              <span className="px-2 py-0.5 rounded-full bg-tc-bg uppercase tracking-wider text-[10px] text-tc-secondary">{CATEGORIES[post.category]}</span>
              <span>{post.author_name}</span>
              <span>·</span>
              <span>{formatTime(post.created_at)}</span>
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-tc-primary leading-tight">{post.title}</h1>
            <p className="text-tc-muted leading-relaxed whitespace-pre-line mt-4">{post.body}</p>
          </div>
        </div>
      </article>

      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-tc-accent mb-4">
        <MessageCircle className="w-3 h-3" /> {replies.length} {replies.length === 1 ? "risposta" : "risposte"}
      </div>

      <div className="space-y-4 mb-10" data-testid="replies-list">
        {replies.map(r => (
          <div key={r.id} className="bg-tc-bg border border-tc/40 rounded-2xl p-5" data-testid={`reply-${r.id}`}>
            <div className="text-xs text-tc-muted mb-2 flex items-center gap-2">
              {r.author_picture && <img src={r.author_picture} alt="" className="w-6 h-6 rounded-full" />}
              <strong className="text-tc-primary">{r.author_name}</strong>
              <span>·</span>
              <span>{formatTime(r.created_at)}</span>
            </div>
            <p className="text-sm text-tc-text whitespace-pre-line leading-relaxed">{r.body}</p>
          </div>
        ))}
      </div>

      {user ? (
        <form onSubmit={sendReply} className="bg-tc-surface rounded-3xl p-6 border border-tc/50" data-testid="reply-form">
          <label className="text-xs uppercase tracking-widest text-tc-muted">La tua voce</label>
          <textarea required rows={4} value={reply} onChange={e => setReply(e.target.value)}
            className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary resize-none mt-1" data-testid="reply-input" />
          <button type="submit" disabled={busy || !reply.trim()} className="btn-primary mt-4" data-testid="reply-submit">
            {busy ? "Invio…" : "Rispondi"}
          </button>
        </form>
      ) : (
        <div className="text-center text-tc-muted">
          <Link to="/login" className="btn-outline">Accedi per rispondere</Link>
        </div>
      )}
    </main>
  );
}
